import React, {useCallback, useContext, useEffect, useState} from "react";
import {Layout, Menu, MenuProps} from 'antd';
import {observer} from "mobx-react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {Translate} from "../util/translation.util";
import {StoreContext} from "../../store.context";
import {HelperService} from "../../service/helper.service";
import styled from "styled-components";

const {Sider} = Layout;

type MenuItem = Required<MenuProps>['items'][number];

const StyledMenuHeader = styled.div`
    height: 190px;

    &:hover {
        cursor: pointer;
    }
`

const StyledMenu = styled.div`
    height: calc(100% - 190px);
    scroll-behavior: smooth;
    overflow-y: auto;

    .ant-menu-inline {
        .ant-menu-item {
            margin: 0;
            padding-top: 4px;
            padding-bottom: 4px;
            width: 100%;
            border-radius: 0;
            font-weight: bold;
            line-height: 50px;
            height: 50px;
        }

        &.ant-menu-root {
            .ant-menu-submenu-title {
                margin: 0;
                padding-top: 4px;
                padding-bottom: 4px;
                width: 100%;
                border-radius: 0;
                font-weight: bold;
                line-height: 50px;
                height: 50px;
            }
        }
    }

    .ant-space-item {
        position: relative;
    }

    .ant-menu-light {
        .ant-menu-item-selected {
            background-color: #bad60a;
            color: white;
        }

        .ant-menu-submenu-open {
            > .ant-menu-submenu-title {
                background-color: #bad60a;
                color: white;
            }
        }

        .ant-menu-sub {
            &.ant-menu-inline {
                box-shadow: inset 0 7px 9px -7px rgba(0, 0, 0, .4), inset 0 -7px 9px -7px rgba(0, 0, 0, .4);
                background-color: white;
            }
        }

        .ant-menu-item:not(.ant-menu-item-selected) {
            a {
                color: #3c4447;
            }
        }
    }
`;

const SiderMenu: React.FC<any> = observer(({visible}) => {
    const [collapsed, setCollapsed] = useState(false);
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

    const {appStore: {accessStore: {checkAccesses}, fetchMenu}} = useContext(StoreContext);

    const navigate = useNavigate();

    let location = useLocation();

    const getItems = useCallback((items: any, accessChecks: any) => {
        items.forEach((item: any) => {
            if (item.bo_path) {
                accessChecks.push({
                    routeName: HelperService.generateRoute(item.bo_path),
                    attributes: []
                })
            }

            if (item.children.length > 0) {
                getItems(item.children, accessChecks);
            }
        });
    }, []);

    useEffect(() => {
        let accessChecks: any = [];
        let menu: any = [];

        fetchMenu().then((data) => {
            getItems(data, accessChecks);

            checkAccesses({accessChecks}).then((accesses) => {
                const checkSubMenuItems = (menuItems: any) => {
                    return menuItems.map((item: any) => {
                        if (item.bo_path) {
                            const access = accesses.find((x: any) => x.routeName === HelperService.generateRoute(item.bo_path));
                            if (access?.allowed) {
                                return {
                                    key: item.key || item.item_enum || item.name,
                                    label: (<Link to={item.bo_uri}><Translate message={item.name}/></Link>),
                                    bo_uri: item.bo_uri || null
                                };
                            }
                        } else if (item.fo_uri) {
                            return {
                                key: item.key || item.item_enum || item.name,
                                label: (<a href={HelperService.generateFoUrl(item.fo_uri)}>
                                    <Translate message={item.name}/>
                                </a>),
                                bo_uri: item.bo_uri || null
                            };
                        }
                        return null;
                    })
                }

                const checkMenuItems = (items: any) => {
                    return items.forEach((item: any) => {
                        //weather it is main route item
                        if (item.bo_path) {
                            const access = accesses.find((x: any) => x.routeName === HelperService.generateRoute(item.bo_path));
                            if (access?.allowed) {
                                menu.push({
                                    key: item.key || item.item_enum || item.name,
                                    label: (<Link to={item.bo_uri}><Translate message={item.name}/></Link>),
                                    bo_uri: item.bo_uri || null
                                });
                            }
                        } else if (item.fo_uri) {
                            menu.push({
                                key: item.key || item.item_enum || item.name,
                                label: (<a href={HelperService.generateFoUrl(item.fo_uri)}>
                                    <Translate message={item.name}/>
                                </a>),
                                bo_uri: item.bo_uri || null
                            });
                        } else {
                            let submenuAccessedItems = checkSubMenuItems(item.children);
                            if (submenuAccessedItems.length > 0) {
                                menu.push({
                                    key: item.key || item.item_enum || item.name,
                                    label: <Translate message={item.name}/>,
                                    children: submenuAccessedItems
                                })
                            }
                        }
                    })
                }

                checkMenuItems(data);
                setMenuItems(menu);
            })
        });


    }, [checkAccesses, fetchMenu, getItems]);

    const fetchMenuKeysByUri = useCallback((menu: any, uri: string): any[] => {
        if (!uri) {
            return [];
        }

        return menu.flatMap((item: any) =>
                (item.children && item.children.length > 0) ?
                        (fetchMenuKeysByUri(item.children, uri).length > 0 ? [item.key, ...fetchMenuKeysByUri(item.children, uri)] : []) :
                        ((item.bo_uri === '/' + uri) ? [item.key] : [])
        );
    }, []);

    const getSelectedGroupAndKey = useCallback(() => {
        const uri = location.pathname.split('/')[1];
        return fetchMenuKeysByUri(menuItems, uri);
    }, [fetchMenuKeysByUri, location.pathname, menuItems]);

    const [openKeys, setOpenKeys] = useState<string[]>(getSelectedGroupAndKey());

    const handleOpenChange = (keys: string[]) => {
        const latestOpenKey = keys.find((key) => !openKeys.includes(key));
        // If there is a newly opened submenu, close others
        if (latestOpenKey) {
            setOpenKeys([latestOpenKey]);
        } else {
            setOpenKeys([]);
        }
    };

    useEffect(() => {
        if (menuItems.length) {
            setOpenKeys(getSelectedGroupAndKey());
        }
    }, [menuItems, location.pathname, getSelectedGroupAndKey])

    useEffect(() => {
        const menuContainer = document.querySelector('.ant-menu');
        const activeMenu = document.querySelector('.ant-menu-submenu-open') || document.querySelector('.ant-menu-item-selected');

        if (menuContainer && activeMenu) {
            requestAnimationFrame(() => {
                activeMenu.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            });
        }
    }, [openKeys]);

    return (
            <Sider
                    collapsed={collapsed}
                    onCollapse={(value) => setCollapsed(value)}
                    theme={"light"}
                    width={visible ? "250px" : "0px"}
                    style={{
                        position: 'fixed',
                        zIndex: 1,
                        height: '100%',
                    }}
            >
                <StyledMenuHeader onClick={() => {
                    navigate('/')
                }} style={!collapsed ? {padding: '50px'} : {padding: '20px 15px'}}>
                    <img src={`/img/cl.logo.svg`} alt={`CLBO`} style={{marginTop: '16px'}}/>
                </StyledMenuHeader>

                <StyledMenu>
                    <Menu
                            defaultOpenKeys={getSelectedGroupAndKey()}
                            selectedKeys={getSelectedGroupAndKey()}
                            onOpenChange={handleOpenChange}
                            openKeys={openKeys}
                            theme="light" mode="inline" items={menuItems}
                    />
                </StyledMenu>
            </Sider>
    )
})

export default SiderMenu;